import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	Divider,
	Drawer,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Slider,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
	type SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SettingsIcon from '@mui/icons-material/Settings';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Palette } from '@mui/material/styles';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import {
	getStageIiiFinalRanking,
	recalculateOlympicsFinalTitles,
	updateOlympicsSettings,
} from '../../../api/content-api';
import Olympics from '../../../model/Olympics';
import Student from '../../../model/Student';
import Loader from '../../../ui/Loader';
import { loginPage } from '../../../store/fixed-routing';
import { useNavigate } from 'react-router-dom';
import { useCustomTheme } from '../../../store/theme-context';
import OlympicsContext from '../../../store/olympics-context';
import { PresentationChromeContext } from '../../../store/presentation-chrome-context';
import { isSmallOlympicVariant } from '../../../config/variant-profile';
import { getBrandVisualAssets } from '../../../config/brand-assets';

const PRESENTATION_PREFS_KEY = 'olijp_final_ranking_presentation_v1';

type TitleColorPresetId = 'theme' | 'white' | 'warm' | 'primary' | 'deep';

type PresentationPrefs = {
	scale: number;
	/** Skala czcionki belki tytułowej (niezależna od tabeli), max 200%. */
	titleScale: number;
	/** Rozmycie znaku wodnego logo (px). Domyślnie ~25% dawnego 22px. */
	logoBlurPx: number;
	/** Max liczba uczniów w widoku prezentacji; 0 = wszyscy. */
	maxStudents: number;
	/** Kolor głównego tytułu w belce (podtytuł lekko przygaszony). */
	titleColorPreset: TitleColorPresetId;
	density: 0 | 1 | 2;
	columns: 1 | 2 | 3;
	/** Widok prezentacji: kolumna „lok.” + kolory wg progów laureatów. */
	showLaureateLokaty: boolean;
};

/** ~25% wcześniejszego domyślnego rozmycia (22px). */
const DEFAULT_LOGO_BLUR_PX = 5.5;

const DEFAULT_PRESENTATION_PREFS: PresentationPrefs = {
	scale: 1,
	titleScale: 1.25,
	logoBlurPx: DEFAULT_LOGO_BLUR_PX,
	maxStudents: 0,
	titleColorPreset: 'theme',
	density: 1,
	columns: 1,
	showLaureateLokaty: false,
};

const TITLE_COLOR_PRESET_OPTIONS: { value: TitleColorPresetId; label: string }[] = [
	{ value: 'theme', label: 'Jak w motywie' },
	{ value: 'white', label: 'Biały' },
	{ value: 'warm', label: 'Ciepła biel' },
	{ value: 'primary', label: 'Kolor primary' },
	{ value: 'deep', label: 'Ciemny fiolet' },
];

function getBannerTitleColors(theme: Theme, preset: TitleColorPresetId): { main: string; sub: string } {
	switch (preset) {
		case 'white':
			return { main: '#ffffff', sub: 'rgba(255,255,255,0.88)' };
		case 'warm':
			return { main: '#fff8f0', sub: 'rgba(255,248,240,0.9)' };
		case 'primary':
			return {
				main: theme.palette.primary.main,
				sub: alpha(theme.palette.primary.main, 0.88),
			};
		case 'deep':
			return { main: '#1a0f28', sub: 'rgba(26,15,40,0.85)' };
		case 'theme':
		default:
			return {
				main: theme.palette.text.primary,
				sub: theme.palette.text.secondary,
			};
	}
}

function loadPresentationPrefs(): PresentationPrefs {
	try {
		const raw = sessionStorage.getItem(PRESENTATION_PREFS_KEY);
		if (!raw) {
			return DEFAULT_PRESENTATION_PREFS;
		}
		const p = JSON.parse(raw) as Partial<PresentationPrefs>;
		const scale =
			typeof p.scale === 'number' ? Math.min(1.5, Math.max(0.65, p.scale)) : DEFAULT_PRESENTATION_PREFS.scale;
		const titleScale =
			typeof p.titleScale === 'number'
				? Math.min(2, Math.max(0.65, p.titleScale))
				: DEFAULT_PRESENTATION_PREFS.titleScale;
		const logoBlurPx =
			typeof p.logoBlurPx === 'number'
				? Math.min(24, Math.max(0, p.logoBlurPx))
				: DEFAULT_PRESENTATION_PREFS.logoBlurPx;
		const density =
			p.density === 0 || p.density === 1 || p.density === 2 ? p.density : DEFAULT_PRESENTATION_PREFS.density;
		const columns =
			p.columns === 1 || p.columns === 2 || p.columns === 3 ? p.columns : DEFAULT_PRESENTATION_PREFS.columns;
		const maxStudents =
			typeof p.maxStudents === 'number' && Number.isFinite(p.maxStudents)
				? Math.min(999, Math.max(0, Math.floor(p.maxStudents)))
				: DEFAULT_PRESENTATION_PREFS.maxStudents;
		const titleColorPreset = TITLE_COLOR_PRESET_OPTIONS.some((o) => o.value === p.titleColorPreset)
			? (p.titleColorPreset as TitleColorPresetId)
			: DEFAULT_PRESENTATION_PREFS.titleColorPreset;
		const showLaureateLokaty =
			typeof p.showLaureateLokaty === 'boolean' ? p.showLaureateLokaty : DEFAULT_PRESENTATION_PREFS.showLaureateLokaty;
		return { scale, titleScale, logoBlurPx, maxStudents, titleColorPreset, density, columns, showLaureateLokaty };
	} catch {
		return DEFAULT_PRESENTATION_PREFS;
	}
}

function savePresentationPrefs(p: PresentationPrefs) {
	try {
		sessionStorage.setItem(PRESENTATION_PREFS_KEY, JSON.stringify(p));
	} catch {
		/* ignore */
	}
}

/** Dzieli listę na N kolumn (wypełnianie jak w gazecie: równo z góry). */
function splitRowsForColumns<T>(items: T[], columnCount: number): { chunk: T[]; startLp: number }[] {
	if (columnCount <= 1 || items.length === 0) {
		return [{ chunk: items, startLp: 0 }];
	}
	const n = items.length;
	const sizes: number[] = [];
	const base = Math.floor(n / columnCount);
	const rem = n % columnCount;
	for (let c = 0; c < columnCount; c++) {
		sizes.push(base + (c < rem ? 1 : 0));
	}
	const out: { chunk: T[]; startLp: number }[] = [];
	let offset = 0;
	for (let c = 0; c < columnCount; c++) {
		const sz = sizes[c];
		out.push({ chunk: items.slice(offset, offset + sz), startLp: offset });
		offset += sz;
	}
	return out;
}

/** Zgodnie z Stage3FinalTitleService::laureateTitleFromFullStageSum — suma IIIA + IIIB. */
type LaureateTier = '' | 'I' | 'II' | 'III';

function laureateTierFromSum(fullSum: number, l1: number, l2: number, l3: number): LaureateTier {
	if (fullSum >= l1) {
		return 'I';
	}
	if (fullSum >= l2 && fullSum < l1) {
		return 'II';
	}
	if (fullSum >= l3 && fullSum < l2) {
		return 'III';
	}
	return '';
}

const LOKATY_ROW_BG: Record<'I' | 'II' | 'III', string> = {
	I: 'rgba(255, 215, 0, 0.16)',
	II: 'rgba(176, 196, 222, 0.28)',
	III: 'rgba(205, 127, 50, 0.2)',
};

const DENSITY_CELL_PY: Record<0 | 1 | 2, number> = {
	0: 0.125,
	1: 0.35,
	2: 0.65,
};

const specLabel = (name: string | undefined) => {
	const n = (name || '').trim();
	if (!n) {
		return '';
	}
	return n.toLowerCase() === 'bez specjalizacji' ? '' : n;
};

type FinalRankingTableContentProps = {
	rows: Student[];
	palette: Palette;
	isHighContrast: boolean;
	stickyHeader?: boolean;
	startLp?: number;
	scale?: number;
	density?: 0 | 1 | 2;
	/** Kolumna „lok.” + podświetlenia — tylko widok prezentacji po włączeniu lokat. */
	showLokColumn?: boolean;
	tierByStudentId?: Map<number, LaureateTier>;
	/** `final_title` — widok podstawowy i eksport; w prezentacji pełnoekranowej wyłączone. */
	showFinalTitleColumn?: boolean;
};

const FinalRankingTableContent = ({
	rows,
	palette,
	isHighContrast,
	stickyHeader = false,
	startLp = 0,
	scale = 1,
	density = 1,
	showLokColumn = false,
	tierByStudentId,
	showFinalTitleColumn = true,
}: FinalRankingTableContentProps) => {
	const py = DENSITY_CELL_PY[density];
	const baseRem = 0.8125;
	return (
		<Table
			size='small'
			stickyHeader={stickyHeader}
			sx={{
				'& .MuiTableCell-root': {
					fontSize: `${baseRem * scale}rem`,
					py,
					px: `${0.5 * scale}rem`,
					lineHeight: density === 0 ? 1.15 : density === 1 ? 1.25 : 1.4,
				},
				'& .MuiTableCell-head': {
					fontSize: `${0.72 * scale}rem`,
					fontWeight: 700,
					backgroundColor: (theme) => (stickyHeader ? alpha(theme.palette.background.paper, 0.97) : undefined),
					backdropFilter: stickyHeader ? 'blur(6px)' : undefined,
					...(stickyHeader && {
						position: 'sticky',
						top: 0,
						zIndex: 3,
						boxShadow: (theme) =>
							`0 1px 0 ${alpha(theme.palette.divider, 0.9)}, 0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
					}),
				},
			}}
		>
			<TableHead>
				<TableRow>
					<TableCell>Lp.</TableCell>
					<TableCell>Imię</TableCell>
					<TableCell>Nazwisko</TableCell>
					<TableCell>Specjalizacja</TableCell>
					<TableCell>Okręg</TableCell>
					<TableCell align='center'>Pisemny</TableCell>
					<TableCell align='center'>Literatura</TableCell>
					<TableCell align='center'>Język</TableCell>
					<TableCell align='center'>Razem</TableCell>
					{showFinalTitleColumn && <TableCell>Tytuł końcowy</TableCell>}
					{showLokColumn && (
						<TableCell
							align='center'
							sx={{ width: '1%', whiteSpace: 'nowrap', px: 0.5, fontWeight: 700 }}
						>
							lok.
						</TableCell>
					)}
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map((s, idx) => {
					const r = s.result;
					const pisemny = Number(r.thirdAStagePoints ?? 0);
					const lit = Number(r.thirdBOneStagePoints ?? 0);
					const jez = Number(r.thirdBTwoStagePoints ?? 0);
					const razem = pisemny + lit + jez;
					const lp = startLp + idx + 1;
					const tier = showLokColumn && tierByStudentId ? (tierByStudentId.get(s.id) ?? '') : '';
					const tierBg = tier === 'I' || tier === 'II' || tier === 'III' ? LOKATY_ROW_BG[tier] : undefined;
					const rowBg = showLokColumn && tierBg ? tierBg : idx % 2 === 1 ? 'action.hover' : 'transparent';
					return (
						<TableRow
							key={s.id}
							sx={{
								bgcolor: rowBg,
								...(showLokColumn && tier && isHighContrast
									? {
											borderLeft: (th) => `3px solid ${th.palette.divider}`,
										}
									: {}),
							}}
						>
							<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit' }}>{lp}</TableCell>
							<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit' }}>{s.firstName}</TableCell>
							<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit' }}>{s.lastName}</TableCell>
							<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit' }}>
								{specLabel(s.specialization?.name)}
							</TableCell>
							<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit' }}>
								{s.district?.name}
							</TableCell>
							<TableCell align='center'>{pisemny}</TableCell>
							<TableCell align='center'>{lit}</TableCell>
							<TableCell align='center'>{jez}</TableCell>
							<TableCell
								align='center'
								sx={{ fontWeight: 'bold' }}
							>
								{razem}
							</TableCell>
							{showFinalTitleColumn && (
								<TableCell sx={{ color: isHighContrast ? palette.primary.light : 'inherit', maxWidth: 280 }}>
									{(r.finalTitle || '').trim()}
								</TableCell>
							)}
							{showLokColumn && (
								<TableCell
									align='center'
									sx={{
										width: '1%',
										whiteSpace: 'nowrap',
										px: 0.5,
										fontWeight: 700,
										color: tier ? 'text.primary' : 'text.disabled',
									}}
								>
									{tier || '—'}
								</TableCell>
							)}
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
};

const FinalStageRankingTable = () => {
	const theme = useTheme();
	const { palette } = theme;
	const { isHighContrast } = useCustomTheme();
	const { token, logout, isAdmin } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const { refreshTablesTrigger, setIsRefreshing, olympics, setOlympics } = useContext(OlympicsContext);
	const { setHideDevChrome } = useContext(PresentationChromeContext);
	const isRefreshingRef = useRef(false);
	const navigate = useNavigate();
	const [rows, setRows] = useState<Student[]>([]);
	const [loading, setLoading] = useState(true);
	const [fullscreenOpen, setFullscreenOpen] = useState(false);
	const [presentationSettingsOpen, setPresentationSettingsOpen] = useState(false);
	const [presPrefs, setPresPrefs] = useState<PresentationPrefs>(() => loadPresentationPrefs());
	const [lokatyDrawerOpen, setLokatyDrawerOpen] = useState(false);
	const [draftLaureate, setDraftLaureate] = useState({ l1: 0, l2: 0, l3: 0 });
	const [savingLokaty, setSavingLokaty] = useState(false);

	const isSmallOlympic = isSmallOlympicVariant();
	const brand = getBrandVisualAssets();
	const brandLogo = brand.logo;
	const leavesBg = brand.presentationBackground;

	const setPrefs = useCallback((next: PresentationPrefs | ((p: PresentationPrefs) => PresentationPrefs)) => {
		setPresPrefs((prev) => {
			const resolved = typeof next === 'function' ? next(prev) : next;
			savePresentationPrefs(resolved);
			return resolved;
		});
	}, []);

	useEffect(() => {
		if (fullscreenOpen) {
			setPresPrefs(loadPresentationPrefs());
		}
	}, [fullscreenOpen]);

	useEffect(() => {
		setHideDevChrome(fullscreenOpen);
		return () => setHideDevChrome(false);
	}, [fullscreenOpen, setHideDevChrome]);

	useEffect(() => {
		if (lokatyDrawerOpen && olympics) {
			setDraftLaureate({
				l1: olympics.thirdStagePoints.partB.laureate1,
				l2: olympics.thirdStagePoints.partB.laureate2,
				l3: olympics.thirdStagePoints.partB.laureate3,
			});
		}
	}, [lokatyDrawerOpen, olympics]);

	useEffect(() => {
		let cancelled = false;
		const isRefresh = refreshTablesTrigger > 0;
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setLoading(true);
		}
		getStageIiiFinalRanking(token)
			.then((response) => {
				if (cancelled) {
					return;
				}
				if (response.status === 200) {
					setRows(response.data.map((d: any) => Student.fromApiResponse(d)));
				} else {
					setMsg({ msg: response.data });
				}
				setLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception: any) => {
				if (cancelled) {
					return;
				}
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
		return () => {
			cancelled = true;
			if (isRefresh && isRefreshingRef.current) {
				isRefreshingRef.current = false;
				setIsRefreshing(false);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, refreshTablesTrigger]);

	const presentationRows = useMemo(() => {
		const lim = presPrefs.maxStudents;
		if (lim == null || lim <= 0) {
			return rows;
		}
		return rows.slice(0, Math.min(lim, rows.length));
	}, [rows, presPrefs.maxStudents]);

	const columnChunks = useMemo(
		() => splitRowsForColumns(presentationRows, presPrefs.columns),
		[presentationRows, presPrefs.columns],
	);

	const effectiveLaureateThresholds = useMemo(() => {
		if (lokatyDrawerOpen) {
			return {
				l1: Number(draftLaureate.l1) || 0,
				l2: Number(draftLaureate.l2) || 0,
				l3: Number(draftLaureate.l3) || 0,
			};
		}
		if (!olympics) {
			return { l1: 0, l2: 0, l3: 0 };
		}
		const b = olympics.thirdStagePoints.partB;
		return { l1: b.laureate1, l2: b.laureate2, l3: b.laureate3 };
	}, [lokatyDrawerOpen, draftLaureate, olympics]);

	const lokatyCounts = useMemo(() => {
		let i = 0;
		let ii = 0;
		let iii = 0;
		const { l1, l2, l3 } = effectiveLaureateThresholds;
		for (const s of presentationRows) {
			const r = s.result;
			const sum =
				Number(r.thirdAStagePoints ?? 0) + Number(r.thirdBOneStagePoints ?? 0) + Number(r.thirdBTwoStagePoints ?? 0);
			const t = laureateTierFromSum(sum, l1, l2, l3);
			if (t === 'I') {
				i++;
			} else if (t === 'II') {
				ii++;
			} else if (t === 'III') {
				iii++;
			}
		}
		return { i, ii, iii };
	}, [presentationRows, effectiveLaureateThresholds]);

	const tierByStudentId = useMemo(() => {
		const m = new Map<number, LaureateTier>();
		if (!fullscreenOpen || !presPrefs.showLaureateLokaty) {
			return m;
		}
		const { l1, l2, l3 } = effectiveLaureateThresholds;
		for (const s of presentationRows) {
			const r = s.result;
			const sum =
				Number(r.thirdAStagePoints ?? 0) + Number(r.thirdBOneStagePoints ?? 0) + Number(r.thirdBTwoStagePoints ?? 0);
			m.set(s.id, laureateTierFromSum(sum, l1, l2, l3));
		}
		return m;
	}, [fullscreenOpen, presPrefs.showLaureateLokaty, presentationRows, effectiveLaureateThresholds]);

	const bannerTitleColors = useMemo(
		() => getBannerTitleColors(theme, presPrefs.titleColorPreset),
		[theme, presPrefs.titleColorPreset],
	);

	const isPresentationListTruncated =
		presPrefs.maxStudents > 0 && rows.length > 0 && presentationRows.length < rows.length;

	const saveLaureateThresholds = useCallback(() => {
		if (!olympics || !token || !isAdmin) {
			return;
		}
		const updated = {
			...olympics,
			thirdStagePoints: {
				...olympics.thirdStagePoints,
				partB: {
					...olympics.thirdStagePoints.partB,
					laureate1: Number(draftLaureate.l1) || 0,
					laureate2: Number(draftLaureate.l2) || 0,
					laureate3: Number(draftLaureate.l3) || 0,
				},
			},
		} as Olympics;
		setSavingLokaty(true);
		updateOlympicsSettings(token, updated)
			.then((response) => {
				if (response.status !== 200) {
					setMsg({ msg: response.data });
					return Promise.reject(new Error('save'));
				}
				setOlympics(Olympics.fromApiResponse(response.data));
				return recalculateOlympicsFinalTitles(token);
			})
			.then((recalcResponse) => {
				if (!recalcResponse || recalcResponse.status !== 200) {
					return;
				}
				const d = recalcResponse.data;
				if (d.skipped && d.message) {
					setMsg({ msg: `Progi zapisane. ${d.message}`, severity: 'info' });
				} else {
					setMsg({
						msg: `Progi zapisane. Przeliczono tytuły (${d.updated} rekordów).`,
						severity: 'success',
					});
				}
				setLokatyDrawerOpen(false);
			})
			.catch((exception: any) => {
				if (exception?.message === 'save') {
					return;
				}
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => setSavingLokaty(false));
	}, [draftLaureate, isAdmin, logout, navigate, olympics, setMsg, setOlympics, token]);

	if (loading) {
		return <Loader />;
	}

	const closeFullscreen = () => {
		setPresentationSettingsOpen(false);
		setLokatyDrawerOpen(false);
		setFullscreenOpen(false);
	};

	const editionLine =
		olympics?.edition && olympics?.schoolYear
			? `Edycja ${olympics.edition} · rok szkolny ${olympics.schoolYear}`
			: olympics?.schoolYear
				? `Rok szkolny ${olympics.schoolYear}`
				: null;

	/** Gradient na wierzchu liści — wyższa krycie niż wcześniej, żeby nie wyglądało jak szary pasek. */
	const bannerTintGradient = isSmallOlympic
		? `linear-gradient(105deg, ${alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.52 : 0.42)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.38)} 48%, ${alpha(theme.palette.secondary.light, theme.palette.mode === 'dark' ? 0.48 : 0.4)} 100%)`
		: `linear-gradient(105deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.5 : 0.4)} 0%, ${alpha(theme.palette.primary.dark, theme.palette.mode === 'dark' ? 0.42 : 0.34)} 52%, ${alpha(theme.palette.success?.main ?? theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.44 : 0.36)} 100%)`;

	const titleScale = presPrefs.titleScale;
	const logoBlurPx = presPrefs.logoBlurPx;
	const logoFilter =
		theme.palette.mode === 'dark' ? `blur(${logoBlurPx}px) brightness(1.15)` : `blur(${logoBlurPx}px) brightness(1.02)`;

	return (
		<Box sx={{ width: '100%', overflowX: 'auto', mt: 1 }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 2,
					mb: 2,
					flexWrap: 'wrap',
				}}
			>
				<Typography
					variant='body2'
					sx={{ color: palette.text.secondary, flex: '1 1 280px' }}
				>
					Kolejność od najwyższej sumy etapu III (IIIA + obie części ustne IIIB). Specjalizacja ukryta dla „Bez
					specjalizacji”.
				</Typography>
				<Tooltip title='Widok prezentacyjny (pełny ekran, ustawienia pod rzutnik)'>
					<IconButton
						color='primary'
						onClick={() => setFullscreenOpen(true)}
						aria-label='Otwórz listę finałową na pełnym ekranie'
						size='small'
						sx={{ flexShrink: 0 }}
					>
						<OpenInFullIcon />
					</IconButton>
				</Tooltip>
			</Box>
			<FinalRankingTableContent
				rows={rows}
				palette={palette}
				isHighContrast={isHighContrast}
			/>

			<Dialog
				fullScreen
				open={fullscreenOpen}
				onClose={closeFullscreen}
				aria-labelledby='final-ranking-fullscreen-title'
				PaperProps={{
					sx: {
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						minHeight: 0,
						bgcolor: 'background.default',
					},
				}}
			>
				<DialogContent
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0,
						overflow: 'hidden',
						p: 0,
						position: 'relative',
						bgcolor: 'background.default',
					}}
				>
					{/* Znak wodny — logo ~80% szerokości, rozmycie ustawialne */}
					<Box
						aria-hidden
						sx={{
							position: 'fixed',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							pointerEvents: 'none',
							zIndex: 0,
							overflow: 'hidden',
						}}
					>
						<Box
							component='img'
							src={brandLogo}
							alt=''
							sx={{
								width: '80%',
								maxWidth: '80vw',
								height: 'auto',
								opacity: theme.palette.mode === 'dark' ? 0.11 : 0.13,
								filter: logoFilter,
								WebkitFilter: logoFilter,
							}}
						/>
					</Box>

					<Box
						sx={{
							position: 'relative',
							zIndex: 1,
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						{/* Nagłówek (belka + ustawienia) — bez przewijania; lista w osobnym bloku poniżej */}
						<Box sx={{ flexShrink: 0 }}>
							{/* Belka: tylko wiersz tytułu ma liście; ustawienia pod spodem bez wzoru */}
							<Box
								sx={{
									borderBottom: 1,
									borderColor: 'divider',
									boxShadow: (t) =>
										t.palette.mode === 'dark'
											? `inset 0 -1px 0 ${alpha(t.palette.common.white, 0.06)}`
											: `inset 0 -1px 0 ${alpha(t.palette.common.black, 0.06)}`,
								}}
							>
								<Box
									sx={{
										position: 'relative',
										overflow: 'hidden',
										minHeight: { xs: 64, sm: 72 },
									}}
								>
									<Box
										aria-hidden
										sx={{
											position: 'absolute',
											inset: 0,
											zIndex: 0,
											overflow: 'hidden',
											pointerEvents: 'none',
										}}
									>
										{/* Grafika pionowa → obrót 90°; repeat + auto = naturalny kafelek, ~1–2 rzędy w pasku */}
										<Box
											sx={{
												position: 'absolute',
												left: '50%',
												top: '50%',
												width: 'min(240vw, 2800px)',
												height: 'min(240vw, 2800px)',
												transform: 'translate(-50%, -50%) rotate(90deg)',
												transformOrigin: 'center center',
												backgroundImage: `url(${leavesBg})`,
												backgroundRepeat: 'repeat',
												backgroundSize: 'auto',
												backgroundPosition: 'center center',
											}}
										/>
									</Box>
									<Box
										aria-hidden
										sx={{
											position: 'absolute',
											inset: 0,
											zIndex: 1,
											background: bannerTintGradient,
											pointerEvents: 'none',
										}}
									/>
									<Box
										sx={{
											position: 'relative',
											zIndex: 2,
											display: 'grid',
											gridTemplateColumns: {
												xs: '44px 44px minmax(0,1fr) 44px',
												sm: '48px 48px minmax(0,1fr) 48px',
											},
											alignItems: 'center',
											columnGap: 0.5,
											px: { xs: 0.75, sm: 1.25 },
											py: { xs: 1, sm: 1.125 },
											minHeight: { xs: 64, sm: 72 },
										}}
									>
										<Tooltip
											title={presentationSettingsOpen ? 'Ukryj ustawienia wyświetlania' : 'Ustawienia wyświetlania'}
										>
											<IconButton
												color='inherit'
												onClick={() => setPresentationSettingsOpen((o) => !o)}
												aria-label='Ustawienia widoku prezentacji'
												aria-expanded={presentationSettingsOpen}
												aria-controls='final-ranking-pres-settings'
												size='small'
												sx={{ justifySelf: 'start' }}
											>
												<SettingsIcon fontSize='small' />
											</IconButton>
										</Tooltip>

										<Tooltip title='Ustalanie lokat — progi Laureata I–III (zapis do ustawień olimpiady)'>
											<IconButton
												color='inherit'
												onClick={() => setLokatyDrawerOpen(true)}
												aria-label='Ustalanie lokat laureatów'
												size='small'
												sx={{ justifySelf: 'start' }}
											>
												<EmojiEventsIcon fontSize='small' />
											</IconButton>
										</Tooltip>

										<Box
											component='h2'
											id='final-ranking-fullscreen-title'
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												gap: { xs: 0.75, sm: 1.25 },
												minWidth: 0,
												flexWrap: 'nowrap',
												m: 0,
												textAlign: 'center',
											}}
										>
											<Typography
												component='span'
												variant='subtitle2'
												sx={{
													fontWeight: 800,
													letterSpacing: { xs: '0.12em', sm: '0.2em' },
													fontSize: {
														xs: `${0.78 * titleScale}rem`,
														sm: `${0.92 * titleScale}rem`,
														md: `${1.02 * titleScale}rem`,
													},
													textTransform: 'uppercase',
													color: bannerTitleColors.main,
													whiteSpace: 'nowrap',
													flexShrink: 0,
													textShadow:
														theme.palette.mode === 'dark'
															? '0 0 12px rgba(0,0,0,0.45)'
															: '0 0 10px rgba(255,255,255,0.65)',
												}}
											>
												Lista finałowa
											</Typography>
											{editionLine && (
												<>
													<Typography
														component='span'
														variant='subtitle2'
														sx={{
															color: bannerTitleColors.sub,
															fontWeight: 500,
															fontSize: {
																xs: `${0.74 * titleScale}rem`,
																sm: `${0.86 * titleScale}rem`,
																md: `${0.94 * titleScale}rem`,
															},
															letterSpacing: '0.02em',
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															minWidth: 0,
															textShadow:
																theme.palette.mode === 'dark'
																	? '0 0 10px rgba(0,0,0,0.4)'
																	: '0 0 8px rgba(255,255,255,0.6)',
														}}
													>
														· {editionLine}
													</Typography>
												</>
											)}
										</Box>

										<Tooltip title='Zamknij'>
											<IconButton
												color='inherit'
												onClick={closeFullscreen}
												aria-label='Zamknij widok pełnoekranowy'
												size='small'
												sx={{ justifySelf: 'end' }}
											>
												<CloseIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</Box>
								</Box>

								<Collapse in={presentationSettingsOpen}>
									<Box
										id='final-ranking-pres-settings'
										role='region'
										aria-label='Ustawienia wyświetlania listy finałowej'
										sx={{
											position: 'relative',
											zIndex: 2,
											px: 2,
											py: 1.5,
											borderTop: 1,
											borderColor: 'divider',
											bgcolor: (t) => alpha(t.palette.background.paper, t.palette.mode === 'dark' ? 0.88 : 0.92),
											backdropFilter: 'blur(10px)',
										}}
									>
										<Typography
											variant='caption'
											color='text.secondary'
											display='block'
											sx={{ mb: 1 }}
										>
											Zapamiętane w tej przeglądarce
										</Typography>
										<Stack
											direction='row'
											alignItems='flex-start'
											spacing={2}
											flexWrap='wrap'
											useFlexGap
										>
											<Box sx={{ minWidth: 140, maxWidth: 200, flex: '1 1 140px' }}>
												<Typography
													variant='caption'
													display='block'
												>
													Skala tekstu tabeli
												</Typography>
												<Slider
													size='small'
													value={presPrefs.scale}
													min={0.65}
													max={1.45}
													step={0.05}
													valueLabelDisplay='auto'
													valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
													onChange={(_, v) => setPrefs((p) => ({ ...p, scale: v as number }))}
													aria-label='Skala tekstu tabeli'
												/>
											</Box>
											<Box sx={{ minWidth: 140, maxWidth: 200, flex: '1 1 140px' }}>
												<Typography
													variant='caption'
													display='block'
												>
													Skala tytułu (belka, do 200%)
												</Typography>
												<Slider
													size='small'
													value={presPrefs.titleScale}
													min={0.65}
													max={2}
													step={0.05}
													valueLabelDisplay='auto'
													valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
													onChange={(_, v) => setPrefs((p) => ({ ...p, titleScale: v as number }))}
													aria-label='Skala czcionki tytułu w belce'
												/>
											</Box>
											<Box sx={{ minWidth: 160, maxWidth: 220, flex: '1 1 160px' }}>
												<Typography
													variant='caption'
													display='block'
												>
													Max. uczniów (0 = wszyscy)
												</Typography>
												<Slider
													size='small'
													value={presPrefs.maxStudents}
													min={0}
													max={200}
													step={5}
													valueLabelDisplay='auto'
													valueLabelFormat={(v) => (v === 0 ? 'Wszyscy' : String(v))}
													onChange={(_, v) => setPrefs((p) => ({ ...p, maxStudents: v as number }))}
													aria-label='Maksymalna liczba uczniów na slajdzie'
												/>
											</Box>
											<Box sx={{ minWidth: 160, maxWidth: 200, flex: '1 1 160px' }}>
												<FormControl
													size='small'
													fullWidth
												>
													<InputLabel id='final-ranking-title-color-label'>Kolor tytułu</InputLabel>
													<Select<TitleColorPresetId>
														labelId='final-ranking-title-color-label'
														label='Kolor tytułu'
														value={presPrefs.titleColorPreset}
														onChange={(e: SelectChangeEvent<TitleColorPresetId>) =>
															setPrefs((p) => ({
																...p,
																titleColorPreset: e.target.value as TitleColorPresetId,
															}))
														}
													>
														{TITLE_COLOR_PRESET_OPTIONS.map((o) => (
															<MenuItem
																key={o.value}
																value={o.value}
															>
																{o.label}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Box>
											<Box sx={{ minWidth: 140, maxWidth: 200, flex: '1 1 140px' }}>
												<Typography
													variant='caption'
													display='block'
												>
													Rozmycie logo (px)
												</Typography>
												<Slider
													size='small'
													value={presPrefs.logoBlurPx}
													min={0}
													max={24}
													step={0.5}
													valueLabelDisplay='auto'
													valueLabelFormat={(v) => `${v}px`}
													onChange={(_, v) => setPrefs((p) => ({ ...p, logoBlurPx: v as number }))}
													aria-label='Rozmycie znaku wodnego logo'
												/>
											</Box>
											<Box sx={{ minWidth: 140, maxWidth: 200, flex: '1 1 140px' }}>
												<Typography
													variant='caption'
													display='block'
												>
													Odstępy między wierszami
												</Typography>
												<Slider
													size='small'
													value={presPrefs.density}
													min={0}
													max={2}
													step={1}
													marks={[
														{ value: 0, label: 'Zwięźle' },
														{ value: 1, label: 'Średnio' },
														{ value: 2, label: 'Luźno' },
													]}
													onChange={(_, v) => setPrefs((p) => ({ ...p, density: v as 0 | 1 | 2 }))}
													aria-label='Odstępy między wierszami'
												/>
											</Box>
											<Box>
												<Typography
													variant='caption'
													display='block'
													mb={0.5}
												>
													Liczba kolumn
												</Typography>
												<ToggleButtonGroup
													exclusive
													size='small'
													value={presPrefs.columns}
													onChange={(_, v) => {
														if (v != null) {
															setPrefs((p) => ({ ...p, columns: v as 1 | 2 | 3 }));
														}
													}}
													aria-label='Liczba kolumn tabeli'
												>
													<ToggleButton value={1}>1</ToggleButton>
													<ToggleButton value={2}>2</ToggleButton>
													<ToggleButton value={3}>3</ToggleButton>
												</ToggleButtonGroup>
											</Box>
											<Button
												size='small'
												variant='text'
												onClick={() => setPrefs(DEFAULT_PRESENTATION_PREFS)}
												sx={{ alignSelf: 'center' }}
											>
												Przywróć domyślne
											</Button>
										</Stack>
									</Box>
								</Collapse>
							</Box>
						</Box>

						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflow: 'auto',
								WebkitOverflowScrolling: 'touch',
								p: { xs: 1.5, sm: 2 },
								position: 'relative',
								zIndex: 1,
							}}
						>
							{isPresentationListTruncated && (
								<Typography
									variant='caption'
									color='text.secondary'
									display='block'
									sx={{ mb: 1 }}
								>
									Wyświetlono {presentationRows.length} z {rows.length} uczniów (limit w ustawieniach).
								</Typography>
							)}
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 2,
									alignItems: 'flex-start',
								}}
							>
								{columnChunks.map(({ chunk, startLp }, ci) => (
									<Box
										key={ci}
										sx={{
											flex: {
												xs: '1 1 100%',
												md:
													presPrefs.columns === 1
														? '1 1 100%'
														: presPrefs.columns === 2
															? '1 1 calc(50% - 8px)'
															: '1 1 calc(33.333% - 11px)',
											},
											minWidth: 0,
											overflowX: 'auto',
										}}
									>
										<FinalRankingTableContent
											rows={chunk}
											palette={palette}
											isHighContrast={isHighContrast}
											stickyHeader
											startLp={startLp}
											scale={presPrefs.scale}
											density={presPrefs.density}
											showFinalTitleColumn={false}
											showLokColumn={presPrefs.showLaureateLokaty}
											tierByStudentId={tierByStudentId}
										/>
									</Box>
								))}
							</Box>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>

			<Drawer
				anchor='right'
				open={lokatyDrawerOpen}
				onClose={() => setLokatyDrawerOpen(false)}
				/* Pełnoekranowy Dialog ma z-index jak Modal (~1300); Drawer domyślnie ~1200 — bez tego panel był niewidoczny „za” dialogiem. */
				sx={{
					zIndex: (theme) => theme.zIndex.modal + 2,
				}}
				ModalProps={{
					keepMounted: false,
					sx: {
						zIndex: (theme) => theme.zIndex.modal + 2,
					},
				}}
				PaperProps={{
					sx: {
						width: { xs: '100%', sm: 400 },
						maxWidth: '100vw',
						p: 2,
						boxSizing: 'border-box',
						zIndex: (theme) => theme.zIndex.modal + 3,
					},
				}}
			>
				<Typography
					variant='h6'
					component='h3'
					gutterBottom
				>
					Ustalanie lokat
				</Typography>
				<Typography
					variant='caption'
					color='text.secondary'
					display='block'
					sx={{ mb: 2 }}
				>
					Progi Laureata I–III: suma punktów etapu III (IIIA + obie części IIIB), jak w Ustawieniach olimpiady. Zapis
					zapisuje je w ustawieniach olimpiady.
				</Typography>

				<FormControlLabel
					control={
						<Switch
							checked={presPrefs.showLaureateLokaty}
							onChange={(_, v) => setPrefs((p) => ({ ...p, showLaureateLokaty: v }))}
							inputProps={{ 'aria-label': 'Pokaż kolumnę lok. i kolory wierszy' }}
						/>
					}
					label='Pokaż lokaty na liście (kolumna „lok.” + kolory)'
				/>

				<Divider sx={{ my: 2 }} />

				<Stack spacing={2}>
					<TextField
						type='number'
						label='Laureat III stopnia (próg od)'
						fullWidth
						size='small'
						value={draftLaureate.l3}
						onChange={(e) =>
							setDraftLaureate((d) => ({
								...d,
								l3: Number(e.target.value),
							}))
						}
						inputProps={{ min: 0, step: 1 }}
					/>
					<TextField
						type='number'
						label='Laureat II stopnia (próg od)'
						fullWidth
						size='small'
						value={draftLaureate.l2}
						onChange={(e) =>
							setDraftLaureate((d) => ({
								...d,
								l2: Number(e.target.value),
							}))
						}
						inputProps={{ min: 0, step: 1 }}
					/>
					<TextField
						type='number'
						label='Laureat I stopnia (próg od)'
						fullWidth
						size='small'
						value={draftLaureate.l1}
						onChange={(e) =>
							setDraftLaureate((d) => ({
								...d,
								l1: Number(e.target.value),
							}))
						}
						inputProps={{ min: 0, step: 1 }}
					/>
				</Stack>

				<Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
					<Typography
						variant='subtitle2'
						gutterBottom
					>
						Podgląd (uczniowie w tym widoku)
					</Typography>
					<Typography variant='body2'>
						Laureat I: <strong>{lokatyCounts.i}</strong>
					</Typography>
					<Typography variant='body2'>
						Laureat II: <strong>{lokatyCounts.ii}</strong>
					</Typography>
					<Typography variant='body2'>
						Laureat III: <strong>{lokatyCounts.iii}</strong>
					</Typography>
				</Box>

				{!isAdmin && (
					<Typography
						variant='caption'
						color='warning.main'
						display='block'
						sx={{ mt: 2 }}
					>
						Tylko administrator może zapisać progi w ustawieniach olimpiady.
					</Typography>
				)}

				<Button
					variant='contained'
					fullWidth
					sx={{ mt: 2 }}
					disabled={!isAdmin || savingLokaty || !olympics}
					onClick={saveLaureateThresholds}
					startIcon={
						savingLokaty ? (
							<CircularProgress
								size={18}
								color='inherit'
							/>
						) : undefined
					}
				>
					{savingLokaty ? 'Zapisywanie…' : 'Zapisz progi w ustawieniach olimpiady'}
				</Button>
			</Drawer>
		</Box>
	);
};

export default FinalStageRankingTable;
